import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { RenderModule } from 'holocron';
import { FormattedMessage } from 'react-intl';
import { PageLevelError } from '@americanexpress/consumer-travel-base';
import { connect, useDispatch } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { Experiment, Variant } from '@americanexpress/one-experiments';
import HotelsSearchBody from './HotelsSearchBody/HotelsSearchBody';
import ErrorsWarning from './ErrorsWarning/ErrorsWarning';
import VariantErrorsWarning from './ErrorsWarning/VariantErrorsWarning';
import hotelsContext from '../context/hotelsContext';
import { MODULE_NAME } from '../constants/globalConstants';
import { getMarketConfig } from '../selectors/marketConfigSelector';
import { getPropOrDefault } from '../utils/marketConfigUtils';
import loadCacheThunk from '../thunks/loadCacheThunk';
import saveHotelsCacheThunk from '../thunks/saveHotelsCacheThunk';
import { getRequest } from '../selectors/requestSelectors';

export function TravelSearchHotelsComponent({
  id,
  request,
  minNights,
  maxAdults,
  maxChildren,
  maxRooms,
  marketConfig,
  taxonomy,
  searchRedirectConfig,
  moduleLoadStatus,
}) {
  const dispatch = useDispatch();
  const moduleWrapperRef = React.useRef(null);

  useEffect(() => {
    dispatch(loadCacheThunk(taxonomy));
  }, [dispatch, taxonomy]);

  useEffect(() => {
    dispatch(saveHotelsCacheThunk(taxonomy));
  }, [dispatch, request, taxonomy]);

  if (moduleLoadStatus === 'error') {
    return (
      <PageLevelError
        description={<FormattedMessage id="error.module_load" />}
        id={id}
      />
    );
  }

  const {
    MIN_NIGHTS,
    MAX_NIGHTS,
    MAX_ADULTS,
    MAX_CHILDREN,
    MAX_ROOMS,
    MIN_ADVANCE_BOOKING,
    MAX_ADVANCE_BOOKING,
    MAX_TRAVELERS_CAP,
    MAX_TRAVELERS_PER_ROOM,
  } = marketConfig;

  const redesignContent = (
    <>
      <VariantErrorsWarning />
      <HotelsSearchBody />
      <RenderModule moduleName="axp-travel-search-redirect" />
    </>
  );

  return (
    <>
      {/* eslint-disable-next-line react/jsx-no-constructed-context-values -- required */}
      <hotelsContext.Provider value={{
        state: {
          moduleWrapperRef,
        },
        config: {
          minNights: getPropOrDefault(minNights, MIN_NIGHTS),
          maxNights: MAX_NIGHTS,
          maxAdults: getPropOrDefault(maxAdults, MAX_ADULTS),
          maxChildren: getPropOrDefault(maxChildren, MAX_CHILDREN),
          maxRooms: getPropOrDefault(maxRooms, MAX_ROOMS),
          minAdvanceBooking: MIN_ADVANCE_BOOKING,
          maxAdvanceBooking: MAX_ADVANCE_BOOKING,
          maxTravelersCap: MAX_TRAVELERS_CAP,
          maxTravelersPerRoom: MAX_TRAVELERS_PER_ROOM,
          moduleId: MODULE_NAME,
          taxonomy,
          searchRedirectConfig,
        },
      }}
      >
        <div
          id={id}
          ref={moduleWrapperRef}
        >
          <Experiment
            experimentName="prebooking_hp_search_widget_redesign"
            dimensionName="search_widget"
            defaultVariant="control"
          >
            <Variant variant="control">
              <ErrorsWarning />
              <HotelsSearchBody />
              <RenderModule moduleName="axp-travel-search-redirect" />
            </Variant>
            <Variant variant="widget_a_with_hero">
              {redesignContent}
            </Variant>
            <Variant variant="widget_a_no_hero">
              {redesignContent}
            </Variant>
            <Variant variant="widget_b_with_hero">
              {redesignContent}
            </Variant>
            <Variant variant="widget_b_no_hero">
              {redesignContent}
            </Variant>
          </Experiment>
        </div>
      </hotelsContext.Provider>
    </>
  );
}

TravelSearchHotelsComponent.propTypes = {
  id: PropTypes.string.isRequired,
  request: PropTypes.shape({}).isRequired,
  minNights: PropTypes.number,
  maxAdults: PropTypes.number,
  maxChildren: PropTypes.number,
  maxRooms: PropTypes.number,
  marketConfig: PropTypes.shape({
    MIN_NIGHTS: PropTypes.number.isRequired,
    MAX_NIGHTS: PropTypes.number.isRequired,
    MAX_ADULTS: PropTypes.number.isRequired,
    MAX_CHILDREN: PropTypes.number.isRequired,
    MAX_ROOMS: PropTypes.number.isRequired,
    MIN_ADVANCE_BOOKING: PropTypes.number.isRequired,
    MAX_ADVANCE_BOOKING: PropTypes.number.isRequired,
    MAX_TRAVELERS_CAP: PropTypes.number.isRequired,
    MAX_TRAVELERS_PER_ROOM: PropTypes.number.isRequired,
  }).isRequired,
  taxonomy: PropTypes.string,
  searchRedirectConfig: PropTypes.shape({}).isRequired,
  moduleLoadStatus: PropTypes.string.isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string,
  }).isRequired,
};

TravelSearchHotelsComponent.defaultProps = {
  minNights: null,
  maxAdults: null,
  maxChildren: null,
  maxRooms: null,
  taxonomy: undefined,
};

export const mapStateToProps = createStructuredSelector({
  marketConfig: getMarketConfig,
  request: getRequest,
});

export default connect(mapStateToProps)(TravelSearchHotelsComponent);
